import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Upload, Loader2, Check, Search, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Convert a picked file to a data URL for upload.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function GalleryPicker({ open, onClose, onSelect, title = 'Choose a graphic' }) {
  const auth = useAuth();
  const fileRef = useRef(null);
  const [tags, setTags] = useState([]);
  const [selTags, setSelTags] = useState([]);
  const [match, setMatch] = useState('any');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(null); // {id, ...ai} newly-uploaded item pending review
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    auth.adminMediaList(selTags, match).then((r) => setItems(Array.isArray(r) ? r : [])).catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    auth.adminMediaTags().then((d) => setTags(d.tags || [])).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selTags, match]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((m) => !q || (m.title || '').toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q) || (m.tags || []).some((t) => t.includes(q)));
  }, [items, search]);

  if (!open) return null;

  const doUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr(''); setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await auth.adminMediaUpload(dataUrl);
      setReview({ id: res.item.id, url: res.item.url, ...res.ai, tags: (res.ai.tags || []).join(', ') });
      load();
    } catch (e2) {
      setErr((e2.response && e2.response.data && e2.response.data.detail) || 'Upload/analysis failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveReview = async () => {
    setUploading(true);
    try {
      await auth.adminMediaUpdate(review.id, {
        title: review.title || '', description: review.description || '',
        filename: review.suggested_filename || '', tags: (review.tags || '').split(',').map((s) => s.trim()).filter(Boolean),
      });
      setReview(null); load();
    } finally { setUploading(false); }
  };

  const pick = (m) => { onSelect(auth.mediaAbsUrl(m.url)); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="gallery-picker">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{title}</h3>
          <button onClick={onClose} data-testid="gallery-picker-close"><X size={18} /></button>
        </div>

        {review ? (
          <div className="p-5 overflow-y-auto" data-testid="gallery-review">
            <div className="flex items-center gap-2 mb-3" style={{ color: 'var(--maroon)' }}><Sparkles size={16} /><span className="font-title">Review — edit before saving</span></div>
            <div className="flex gap-4">
              <img src={auth.mediaAbsUrl(review.url)} alt="" className="w-40 h-40 object-cover rounded border" style={{ borderColor: 'rgba(0,0,0,0.15)' }} />
              <div className="flex-1 space-y-2">
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Title</label><input value={review.title || ''} onChange={(e) => setReview({ ...review, title: e.target.value })} data-testid="gallery-review-title" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Description</label><textarea value={review.description || ''} onChange={(e) => setReview({ ...review, description: e.target.value })} rows={2} data-testid="gallery-review-desc" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Tags (comma-separated)</label><input value={review.tags || ''} onChange={(e) => setReview({ ...review, tags: e.target.value })} data-testid="gallery-review-tags" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
                <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Suggested file name</label><input value={review.suggested_filename || ''} onChange={(e) => setReview({ ...review, suggested_filename: e.target.value })} data-testid="gallery-review-filename" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
                {review.detected_text ? <p className="text-xs" style={{ color: 'var(--olive)' }}>Text detected in image: “{review.detected_text}”</p> : null}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { pick({ url: review.url }); }} className="px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)' }} data-testid="gallery-review-useasis">Use as-is</button>
              <button onClick={saveReview} disabled={uploading} data-testid="gallery-review-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{uploading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save details</button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-2 border-b space-y-2" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} data-testid="gallery-upload-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload new
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" onChange={doUpload} className="hidden" data-testid="gallery-file-input" />
                  {uploading && <span className="text-xs" style={{ color: 'var(--olive)' }}>Uploading & analysing…</span>}
                </div>
                <div className="relative">
                  <Search size={13} className="absolute left-2 top-2" style={{ color: 'var(--olive)' }} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="search" data-testid="gallery-search" className="text-sm pl-7 pr-2 py-1.5 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {tags.map((t) => {
                  const on = selTags.includes(t);
                  return <button key={t} onClick={() => setSelTags((s) => on ? s.filter((x) => x !== t) : [...s, t])} data-testid={`gallery-tag-${t}`}
                    className="text-xs px-2 py-0.5 rounded-full border" style={{ background: on ? 'var(--maroon)' : 'transparent', color: on ? 'var(--cream)' : 'var(--ink)', borderColor: 'var(--maroon)' }}>{t}</button>;
                })}
                {selTags.length > 1 && (
                  <select value={match} onChange={(e) => setMatch(e.target.value)} data-testid="gallery-match" className="text-xs px-1.5 py-0.5 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                    <option value="any">any tag</option><option value="all">all tags</option>
                  </select>
                )}
                {selTags.length > 0 && <button onClick={() => setSelTags([])} className="text-xs underline" style={{ color: 'var(--olive)' }} data-testid="gallery-clear-tags">clear</button>}
              </div>
              {err && <p className="text-xs" style={{ color: '#8a1f11' }} data-testid="gallery-error">{err}</p>}
            </div>

            <div className="p-4 overflow-y-auto flex-1" data-testid="gallery-grid">
              {loading ? <div className="text-center py-10"><Loader2 className="animate-spin inline" size={20} /></div>
              : filtered.length === 0 ? <p className="text-center py-10 text-sm" style={{ color: 'var(--olive)' }}>No graphics{selTags.length ? ' for these tags' : ''} yet. Upload one to get started.</p>
              : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {filtered.map((m) => (
                    <button key={m.id} onClick={() => pick(m)} title={m.title || m.description} data-testid={`gallery-item-${m.id}`}
                      className="group relative rounded overflow-hidden border hover:shadow-md transition-shadow" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                      <img src={auth.mediaAbsUrl(m.url)} alt={m.title || ''} className="w-full h-24 object-cover" loading="lazy" />
                      <div className="px-1.5 py-1 text-[10px] text-left truncate" style={{ color: 'var(--navy)' }}>{m.title || (m.tags || []).slice(0, 2).join(', ') || 'untitled'}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
